export type DuplicateReviewStatus = "pending" | "confirmed" | "cleared";

export interface DuplicateReviewCounts {
  pending: number;
  confirmed: number;
  cleared: number;
  total: number;
}

export interface DuplicateReviewQueueState {
  items: any[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  counts: DuplicateReviewCounts;
}

const asNumber = (value: any, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const emptyDuplicateReviewCounts = (): DuplicateReviewCounts => ({
  pending: 0,
  confirmed: 0,
  cleared: 0,
  total: 0
});

/**
 * The API keeps its legacy bare-array response for deploy-order safety. This
 * normalizer lets the new dashboard consume the paginated contract while
 * still rendering against an older API during a rolling staging deploy.
 */
export const normalizeDuplicateReviewQueueResponse = (
  payload: any,
  fallbackPage: number,
  fallbackLimit: number
): DuplicateReviewQueueState => {
  if (Array.isArray(payload)) {
    const pending = payload.filter(
      row => (row.status || "pending") === "pending"
    ).length;
    return {
      items: payload,
      page: fallbackPage,
      limit: fallbackLimit,
      total: payload.length,
      totalPages: payload.length
        ? Math.ceil(payload.length / fallbackLimit)
        : 0,
      counts: {
        pending,
        confirmed: payload.filter(row => row.status === "confirmed").length,
        cleared: payload.filter(row => row.status === "cleared").length,
        total: payload.length
      }
    };
  }

  const pagination = (payload && payload.pagination) || {};
  const rawCounts = (payload && payload.counts) || {};
  const counts = {
    pending: asNumber(rawCounts.pending),
    confirmed: asNumber(rawCounts.confirmed),
    cleared: asNumber(rawCounts.cleared),
    total: asNumber(rawCounts.total)
  };
  return {
    items: payload && Array.isArray(payload.items) ? payload.items : [],
    page: asNumber(pagination.page, fallbackPage),
    limit: asNumber(pagination.limit, fallbackLimit),
    total: asNumber(pagination.total),
    totalPages: asNumber(pagination.totalPages),
    counts
  };
};

export const canConfirmDuplicate = (row: any): boolean =>
  (row.status || "pending") === "pending";

export const canClearDuplicate = (row: any): boolean => {
  const status = row.status || "pending";
  return status === "pending" || status === "confirmed";
};

export const applyDuplicateResolution = (
  rows: any[],
  result: any,
  action: "confirm" | "clear",
  statusFilter: string
): any[] => {
  const resolvedId = Number(result && result.id);
  const hygienistId = Number(result && result.hygienistId);
  const nextStatus: DuplicateReviewStatus =
    (result && result.status) ||
    (action === "confirm" ? "confirmed" : "cleared");
  const nextTier = result && result.holdState && result.holdState.tier;

  return rows.reduce((nextRows: any[], row: any) => {
    const isResolvedRow = Number(row.id) === resolvedId;
    if (
      isResolvedRow &&
      statusFilter !== "all" &&
      statusFilter !== nextStatus
    ) {
      return nextRows;
    }

    const nextRow = { ...row };
    if (
      hygienistId > 0 &&
      Number(row.hygienistId) === hygienistId &&
      nextTier
    ) {
      nextRow.holdTier = nextTier;
    }
    if (isResolvedRow) {
      nextRow.status = nextStatus;
      nextRow.resolvedAt = new Date().toISOString();
    }
    nextRows.push(nextRow);
    return nextRows;
  }, []);
};

export const applyDuplicateResolutionCounts = (
  counts: DuplicateReviewCounts,
  previousStatus: DuplicateReviewStatus,
  nextStatus: DuplicateReviewStatus
): DuplicateReviewCounts => {
  const next = { ...counts };
  if (previousStatus !== nextStatus) {
    next[previousStatus] = Math.max(0, asNumber(next[previousStatus]) - 1);
    next[nextStatus] = asNumber(next[nextStatus]) + 1;
  }
  next.total = next.pending + next.confirmed + next.cleared;
  return next;
};
